import React, { useContext, useEffect, useState } from 'react';
import { TrueFalseActivity as TrueFalseActivityType } from '../../types';
import Button from '../common/Button';
import { ArrowRight, Check, Mic, Sparkles, X } from 'lucide-react';
import { useSpeechRecognition } from '../../hooks/useSpeechRecognition';
import { useNavigate } from 'react-router-dom';
import { PracticeModal } from '../PracticeModal';
import { UserContext } from '../../store/useStore';
import { useAuth } from '../../contexts/AuthContext';
import { updateUserScores } from '../../services/leaderboardService';
import { incrementChallengeScore } from '../../services/challengeService';
import { logUserActivity } from '../../services/activityLogService';

interface SpeakActivityProps {
  onComplete: () => void;
  setTrueFalseActivity: (value: boolean) => void;
  saveDayExercise: (activity: string) => void;
  getDayExercise: (activity: string, word: object) => Promise<boolean>;
  setShowReward: (value: boolean) => void;
  allActivitiesCompleted: boolean;
  setPoints: (value: number) => void;
  onPointsEarned: (points: number) => void;
  currentWord: object | undefined | null;
}

const SpeakActivity: React.FC<SpeakActivityProps> = ({allActivitiesCompleted, setPoints, onPointsEarned, saveDayExercise, onComplete, currentWord, getDayExercise, setShowReward, setTrueFalseActivity }) => {
  const [selectedExample, setSelectedExample] = useState({});
    const { isRecording, transcript, startRecording, stopRecording } = useSpeechRecognition({
      isFromChatInput: false,
      isFromPractice: true
    });
  const navigate = useNavigate();
  const { updateWordProgress } = useContext(UserContext);
  const [score, setScore] = useState(null);
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const { currentUser } = useAuth();

  const checkExercise = async () => {
    const isCompleted = await getDayExercise('speak', currentWord);
    if (isCompleted) {
      setHasSubmitted(true);
    } else {
      setHasSubmitted(false);
    }
  }

  const initialPracticeState = {
      isRecording: false,
      transcription: '',
      audioBlob: null,
      attempts: 0,
      score: null,
    };
  
    const [practiceState, setPracticeState] = useState(initialPracticeState);
  
    const handleStartRecording = () => {
      startRecording();
      setPracticeState(prevState => ({
        ...prevState,
        attempts: prevState.attempts + 1,
      }));
    };

    useEffect(() => {
        setPracticeState(prevState => ({
          ...prevState,
          isRecording: isRecording,
          transcription: transcript,
        }));
      }, [isRecording, transcript]);

    useEffect(() => {
      if (currentWord) {
        console.log('text', currentWord.details.examples[0])
        const exampleObject = {
          text: currentWord.details.examples[0],
        };
        setSelectedExample(exampleObject);
      }
    }, [currentWord]);

    useEffect(() => {
      if(selectedExample) {
        console.log('selectedExample:', selectedExample); 
      }
    }, [selectedExample]);

  const handleSubmit = () => {
    if (hasSubmitted) return;
    
    setHasSubmitted(true);
    saveDayExercise('speak');
    const earned = score ? Math.round(score) : 0;
    onPointsEarned(earned);
    setPoints(earned);
    setShowReward(true);
    
    setTimeout(() => {
      setShowReward(false);
      onComplete();
    }, 1500);
  };

  const handleReset = () => {
    setHasSubmitted(false);
  };

  const handleClosePractice = () => {
    setSelectedExample(null);
    setPracticeState(initialPracticeState);
    stopRecording();
  };

  useEffect(() => {
    checkExercise();
  }, []);

  useEffect(() => {
    console.log('selectedExample:', selectedExample);
  }, [selectedExample]);

  if(!selectedExample) {
    return (<div className="bg-white rounded-xl shadow-md p-12 flex justify-center items-center h-screen">
      <div className="animate-spin h-8 w-8 border-4 border-blue-500 rounded-full border-t-transparent"></div>
    </div>)
  }

  return (
    <div className="bg-white rounded-xl shadow-md border border-gray-100 p-3 sm:p-6">
      <h3 className="text-lg sm:text-xl font-semibold text-gray-800 mb-3 sm:mb-4">Practice your speaking</h3>
      
      {selectedExample && (<PracticeModal
                  example={selectedExample}
                  practiceState={practiceState}
                  onClose={handleClosePractice}
                  onStartRecording={handleStartRecording}
                  onStopRecording={stopRecording}
                  isRecording={isRecording}
                  activity={true}
                  setScoreActivity={setScore}
                  disableTryAgain={hasSubmitted}
                />)}
      
      
      {!hasSubmitted && score !== null && !isRecording && (
          <Button 
            onClick={handleSubmit} 
            disabled={hasSubmitted}
            className='float-right'
          >
            Next
          </Button>
        )}

      {hasSubmitted && !allActivitiesCompleted && (
              <div className="mt-5 bg-purple-50 border border-purple-100 rounded-xl p-6 mb-8">
              <div className="flex flex-col items-center text-center md:flex-row md:justify-between md:items-center md:text-left">
                {/* Icon + Text Section */}
                <div className="flex flex-col items-center md:flex-row md:items-center">
                  <div className="bg-purple-100 rounded-full p-3 mb-2 md:mb-0 md:mr-4">
                    <Sparkles className="h-6 w-6 text-purple-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-purple-900">All activities completed!</h3>
                    <p className="text-purple-700 mt-2">Great job! Try a new word to continue learning.</p>
                  </div>
                </div>
            
                {/* Button Section */}
                <div className="mt-4 md:mt-0 md:ml-auto">
                  <Button
                    onClick={() => navigate('/themes')}
                    className="bg-purple-600 text-white px-4 py-2 rounded-md hover:bg-purple-400 transition duration-200"
                    rightIcon={<ArrowRight className="h-4 w-4" />}
                  >
                    New Word
                  </Button>
                </div>
              </div>
            </div>    
            )}
        
        {hasSubmitted && !allActivitiesCompleted && (
              <div className="mt-1 bg-gray-50 border border-purple-400 rounded-xl p-6 mb-8">
                <div className="flex flex-col items-center text-center md:flex-row md:justify-between md:items-center md:text-left">
                  {/* Icon + Text Section */}
                  <div className="flex flex-col items-center md:flex-row md:items-center">
                    <div className="bg-purple-700 rounded-full p-3 mb-2 md:mb-0 md:mr-4">
                      <Mic className="h-6 w-6 text-white" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-purple-900">Continue practicing the word</h3>
                      <p className="text-purple-700 mt-2">Go to the speak page to continue improving your speaking</p>
                    </div>
                  </div>
              
                  {/* Button Section */}
                  <div className="mt-4 md:mt-0 md:ml-auto">
                    <Button
                      onClick={() => navigate('/speaking')}
                      className="border border-purple-600 px-4 py-2 rounded-md hover:bg-gray-400 transition duration-200"
                      rightIcon={<ArrowRight className="h-4 w-4" />}
                    >
                      Practice
                    </Button>
                  </div>
                </div>
              </div>    
            )}
    </div>
  );
};

export default SpeakActivity;